/**
 * Leave balance administration — the HR register, manual adjustments, bulk CSV adjustments, and
 * the per-person balance ledger an employee reads back.
 *
 * A SEPARATE ROUTER FROM `leave.contract.ts`, same `/leave` prefix, no path overlap: that file is
 * the employee-facing request lifecycle; this one is HR operating on other people's balances, and
 * answers to its own permission (`leave.balance:adjust`). `getBalanceHistory` lives here too
 * because it is the read side of the same ledger the adjustments write to.
 *
 * ADJUSTMENTS ARE LEDGER ENTRIES, NEVER AN OVERWRITE (CLAUDE.md invariant 5). `leave_balances` is
 * already an effective-dated running-total ledger: every accrual, debit, credit and carry-forward
 * closes the current row and opens a new one. An adjustment is one more such row, with
 * `reason = 'adjustment'`, and the HR-facing facts (direction, reason text, effective date, who)
 * live on a companion `leave_balance_adjustments` record pointing at it. So an adjustment shows up
 * in `getBalanceHistory` next to the accruals and debits it sits between, with nothing to reconcile.
 *
 * THE LEAVE YEAR IS DERIVED, NEVER SUPPLIED (CLAUDE.md invariant 3). A caller names the
 * `effectiveDate`; the server resolves which leave year that date falls in from the person's legal
 * entity `fiscalYearStartMonth` and the leave type's `leaveYearBasis` (a calendar-basis type resets
 * every January regardless of the entity). An effective date after "today" at the person's
 * location is refused: the ledger records what has happened, not what is scheduled to.
 *
 * DAYS ARE STRINGS. Adjustment amounts use `leaveDaysSchema` (positive, whole or half day). Balance
 * figures use `signedDaysSchema` below, because an accrual can land on a quarter day and a
 * historical over-debit can leave a balance below zero — the register must show both honestly
 * rather than fail to parse its own data.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  leaveDaysSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** A day count that may be fractional or negative — a balance figure, never an input amount. */
export const signedDaysSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'Must be a decimal day count');

/** credit adds days to the balance, debit removes them. The amount itself is always positive. */
export const leaveBalanceAdjustmentDirectionSchema = z.enum(['credit', 'debit']);
export type LeaveBalanceAdjustmentDirection = z.infer<typeof leaveBalanceAdjustmentDirectionSchema>;

/** What produced a ledger row — the `LeaveBalance.reason` column, as written by every writer. */
export const leaveBalanceEntryKindSchema = z.enum([
  'opening',
  'accrual',
  'debit',
  'credit',
  'adjustment',
  'carry_forward',
  // Leave encashment (ADR 0031). `encashment` and `lapse` remove days; `encashment_reversal`
  // returns the days of a withdrawn or rejected request.
  'encashment',
  'lapse',
  'encashment_reversal',
]);
export type LeaveBalanceEntryKind = z.infer<typeof leaveBalanceEntryKindSchema>;

// ---------------------------------------------------------------------------
// Register (HR's view of everyone's balances)
// ---------------------------------------------------------------------------

/**
 * One leave type's position for one person in one leave year.
 * `availableDays = accruedDays + adjustedDays - usedDays - pendingDays`, where `pendingDays` is the
 * paid portion of requests still awaiting approval (already held against the balance at filing).
 * `hasLedger` is false when no balance row was ever opened — every figure is then zero.
 */
export const leaveBalanceRegisterEntrySchema = z.object({
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  leaveYear: z.number().int(),
  accruedDays: signedDaysSchema,
  adjustedDays: signedDaysSchema,
  usedDays: signedDaysSchema,
  pendingDays: signedDaysSchema,
  availableDays: signedDaysSchema,
  /** Days paid out (net of reversals) and days written off in this leave year, so the register
   * reconciles against the ledger's `encashment` / `lapse` entries. '0' when there are none. */
  encashedDays: signedDaysSchema.default('0'),
  lapsedDays: signedDaysSchema.default('0'),
  hasLedger: z.boolean(),
});
export type LeaveBalanceRegisterEntry = z.infer<typeof leaveBalanceRegisterEntrySchema>;

/** One employee in the register, with every balance the filters selected for them. */
export const leaveBalanceRegisterRowSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  balances: z.array(leaveBalanceRegisterEntrySchema),
});
export type LeaveBalanceRegisterRow = z.infer<typeof leaveBalanceRegisterRowSchema>;

export const leaveBalanceRegisterQuerySchema = paginationQuerySchema.extend({
  /** Absent means each balance's own CURRENT leave year (per entity and leave-type basis). */
  leaveYear: z.coerce.number().int().min(2000).max(2100).optional(),
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  leaveTypeId: uuidSchema.optional(),
  /** Matches first name, last name, or employee code, case-insensitively. */
  search: z.string().trim().max(100).optional(),
});

// ---------------------------------------------------------------------------
// Adjustments
// ---------------------------------------------------------------------------

/** One recorded adjustment — the companion record to its `reason = 'adjustment'` ledger row. */
export const leaveBalanceAdjustmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  leaveYear: z.number().int(),
  direction: leaveBalanceAdjustmentDirectionSchema,
  days: leaveDaysSchema,
  effectiveDate: dateOnlySchema,
  reason: z.string(),
  balanceBeforeDays: signedDaysSchema,
  balanceAfterDays: signedDaysSchema,
  /** Set when the adjustment came from a bulk CSV apply — every row of one upload shares it. */
  batchId: uuidSchema.nullable(),
  adjustedByPersonId: uuidSchema.nullable(),
  adjustedByName: z.string().nullable(),
  createdAt: instantSchema,
});
export type LeaveBalanceAdjustment = z.infer<typeof leaveBalanceAdjustmentSchema>;

export const adjustLeaveBalanceBodySchema = z
  .object({
    personId: uuidSchema,
    leaveTypeId: uuidSchema,
    direction: leaveBalanceAdjustmentDirectionSchema,
    // `leaveDaysSchema` itself now allows up to 2 decimal places (an hourly request's
    // day-equivalent, e.g. "0.38" — see that schema's own comment); a manual HR adjustment is
    // still whole/half day only, per this file's header, so that finer precision is refused here.
    days: leaveDaysSchema.refine(
      (value) => Number.parseFloat(value) * 2 === Math.round(Number.parseFloat(value) * 2),
      'Must be a whole or half day amount',
    ),
    effectiveDate: dateOnlySchema,
    /** Mandatory: an adjustment nobody can explain later is the thing an audit exists to catch. */
    reason: z.string().trim().min(3).max(500),
  })
  .strict();

// ---------------------------------------------------------------------------
// Bulk (CSV) adjustments
// ---------------------------------------------------------------------------

/**
 * The CSV's required header: `employee_code,leave_type_code,direction,days,effective_date,reason`.
 * Column order is free; names are matched case-insensitively. Capped well below anything a
 * browser upload of a real org would exceed, so one request stays one transaction.
 */
export const LEAVE_BALANCE_CSV_COLUMNS = [
  'employee_code',
  'leave_type_code',
  'direction',
  'days',
  'effective_date',
  'reason',
] as const;
export const LEAVE_BALANCE_CSV_MAX_ROWS = 2000;

export const bulkLeaveBalanceAdjustmentBodySchema = z
  .object({
    /** The CSV file's text content, header row first. */
    csv: z.string().min(1).max(500_000),
  })
  .strict();

/** One CSV row after validation. Every resolved field is null when that column failed to parse. */
export const bulkLeaveBalanceAdjustmentRowSchema = z.object({
  /** 1-based, counting the header as row 1 — the number a spreadsheet shows beside the line. */
  rowNumber: z.number().int().positive(),
  employeeCode: z.string(),
  leaveTypeCode: z.string(),
  personId: uuidSchema.nullable(),
  personName: z.string().nullable(),
  leaveTypeId: uuidSchema.nullable(),
  leaveYear: z.number().int().nullable(),
  direction: leaveBalanceAdjustmentDirectionSchema.nullable(),
  days: leaveDaysSchema.nullable(),
  effectiveDate: dateOnlySchema.nullable(),
  reason: z.string().nullable(),
  /** Balance before/after this row, accounting for earlier rows of the same file. */
  balanceBeforeDays: signedDaysSchema.nullable(),
  balanceAfterDays: signedDaysSchema.nullable(),
  errors: z.array(z.string()),
});
export type BulkLeaveBalanceAdjustmentRow = z.infer<typeof bulkLeaveBalanceAdjustmentRowSchema>;

export const bulkLeaveBalanceAdjustmentPreviewSchema = z.object({
  /** File-level problems (missing columns, too many rows). Rows are empty when any is present. */
  fileErrors: z.array(z.string()),
  rows: z.array(bulkLeaveBalanceAdjustmentRowSchema),
  validCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
});
export type BulkLeaveBalanceAdjustmentPreview = z.infer<
  typeof bulkLeaveBalanceAdjustmentPreviewSchema
>;

export const bulkLeaveBalanceAdjustmentResultSchema = z.object({
  batchId: uuidSchema,
  appliedCount: z.number().int().nonnegative(),
  adjustments: z.array(leaveBalanceAdjustmentSchema),
});
export type BulkLeaveBalanceAdjustmentResult = z.infer<
  typeof bulkLeaveBalanceAdjustmentResultSchema
>;

// ---------------------------------------------------------------------------
// History (the ledger, as the person — or HR — reads it)
// ---------------------------------------------------------------------------

export const leaveBalanceHistoryEntrySchema = z.object({
  /** The ledger row id. */
  id: uuidSchema,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  leaveYear: z.number().int(),
  kind: leaveBalanceEntryKindSchema,
  /** Signed: positive added days, negative removed them. */
  changeDays: signedDaysSchema,
  balanceAfterDays: signedDaysSchema,
  /** The day the ledger row was opened. */
  recordedOn: dateOnlySchema,
  recordedAt: instantSchema,
  leaveRequestId: uuidSchema.nullable(),
  /** Present only for `kind = 'adjustment'`. */
  adjustment: z
    .object({
      id: uuidSchema,
      direction: leaveBalanceAdjustmentDirectionSchema,
      effectiveDate: dateOnlySchema,
      reason: z.string(),
      adjustedByName: z.string().nullable(),
      batchId: uuidSchema.nullable(),
    })
    .nullable(),
});
export type LeaveBalanceHistoryEntry = z.infer<typeof leaveBalanceHistoryEntrySchema>;

export const leaveBalanceHistoryQuerySchema = paginationQuerySchema.extend({
  /** Whose ledger. Defaults to the caller; anyone else needs leave.balance:read over them. */
  personId: uuidSchema.optional(),
  leaveTypeId: uuidSchema.optional(),
  leaveYear: z.coerce.number().int().min(2000).max(2100).optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const leaveBalanceAdminContract = c.router(
  {
    getBalanceRegister: {
      method: 'GET',
      path: '/balance-register',
      query: leaveBalanceRegisterQuerySchema,
      responses: {
        200: paginatedSchema(leaveBalanceRegisterRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "Every in-scope employee's balance per leave type for a leave year, with accrued / " +
        'adjusted / used / pending / available (leave.balance:adjust)',
      metadata: routeMeta('leave.balance:adjust'),
    },

    getBalanceHistory: {
      method: 'GET',
      path: '/balance-history',
      query: leaveBalanceHistoryQuerySchema,
      responses: {
        200: paginatedSchema(leaveBalanceHistoryEntrySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "A person's leave balance ledger, newest first — accruals, debits, credits, carry-" +
        'forwards and HR adjustments with their reasons',
      metadata: routeMeta('leave.balance:read'),
    },

    adjustBalance: {
      method: 'POST',
      path: '/balance-adjustments',
      body: adjustLeaveBalanceBodySchema,
      responses: {
        201: leaveBalanceAdjustmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Credit or debit one person's leave balance with a reason and effective date, recorded " +
        'as a ledger entry (leave.balance:adjust)',
      metadata: routeMeta('leave.balance:adjust'),
    },

    // Both bulk routes carry the CSV's text in the body — a file's content, which a model cannot
    // meaningfully produce (meta.ts reason 2). The single `adjustBalance` stays a copilot tool.
    previewBulkAdjustments: {
      method: 'POST',
      path: '/balance-adjustments/bulk/preview',
      body: bulkLeaveBalanceAdjustmentBodySchema,
      responses: {
        200: bulkLeaveBalanceAdjustmentPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Validate a leave-balance adjustment CSV and preview each row, writing nothing',
      metadata: routeMeta('leave.balance:adjust', { ai: 'hidden' }),
    },

    applyBulkAdjustments: {
      method: 'POST',
      path: '/balance-adjustments/bulk',
      body: bulkLeaveBalanceAdjustmentBodySchema,
      responses: {
        201: bulkLeaveBalanceAdjustmentResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        // Any invalid row refuses the whole file — the preview says which, and nothing is applied.
        422: errorBodySchema,
      },
      summary:
        'Apply every row of a validated leave-balance adjustment CSV atomically ' +
        '(leave.balance:adjust, Idempotency-Key required)',
      metadata: routeMeta('leave.balance:adjust', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/leave' },
);
